import {Component, inject, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationService} from '../../_auth/authentication.service';
import {SecondFactorHttpService} from '../../_auth/two-factor/second-factor-http.service';
import {describeSecondFactorError} from '../../_auth/two-factor/second-factor-errors';
import {
  CONTEXT_ENTRY_REFUSAL_PARAM,
  CONTEXT_ENTRY_REFUSAL_VALUE
} from '../../_auth/context/context-entry-refusal';
import {AppRoutes} from '../../enums/routes';
import {TwoFactorPanelComponent} from '../profile/two-factor-panel/two-factor-panel.component';

/**
 * The remediation screen for an **account-security restricted** session (#282) — the two-factor twin of
 * `FormsCompletionComponent`.
 *
 * A session lands here when its company requires a second factor and it has proven only a password:
 * `AuthGuard` sends it here from every other route, and `ErrorInterceptor` sends it here from every 403 the
 * gate raises. It is therefore the only screen such a session can see, which sets the two things it must do
 * and the one it must not:
 *
 * * It must **enrol** — done by reusing {@link TwoFactorPanelComponent} rather than a second copy of that flow.
 * * It must **re-issue the session** afterwards, so the restriction lifts without a re-login. That goes through
 *   `AuthenticationService.applyReissuedSession(...)`, the mechanism the forms gate already uses — a second
 *   "apply" would be a second place for the merge semantics (keep the tenant, take the new claims) to drift.
 * * It must **not** offer a way back into the app. The only exits are a completed enrolment and signing out.
 *
 * It deliberately renders outside every panel shell (see `AppRoutes.TwoFactorRequired`): `AccountSecurityLevel`
 * is a company-wide policy, so this screen is reached by employees, shard admins, company owners and platform
 * owners alike, and none of their shells would frame it correctly — the navigation in them all leads to routes
 * the guard is going to bounce straight back here.
 */
@Component({
  selector: 'app-two-factor-required',
  standalone: true,
  imports: [TwoFactorPanelComponent],
  templateUrl: './two-factor-required.component.html'
})
export class TwoFactorRequiredComponent {
  private readonly auth = inject(AuthenticationService);
  private readonly secondFactor = inject(SecondFactorHttpService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /**
   * The screen was opened by a **refused context entry** (#279) rather than by a restricted session (#282).
   *
   * Two situations arrive here and almost everything about them differs, so the screen cannot assume the one it
   * was written for:
   *
   * | | restricted session (#282) | refused context entry (#279) |
   * | --- | --- | --- |
   * | The session | carries `acct_sec = restricted` | carries no claim at all |
   * | What it may reach | this screen and nothing else | anything a contextless session may — including other tenants |
   * | To leave the gate | re-issue the token | nothing; the next entry re-reads the factor server-side |
   *
   * Read from the URL because the entry surfaces navigate with `window.open(url, '_self')` — a full document
   * load, since entering a context can change which shell is correct — so nothing held in memory survives the
   * trip. A snapshot rather than the observable: this component is created by that navigation and the value
   * cannot change while it lives.
   */
  readonly refusedAtContextEntry =
    this.route.snapshot.queryParamMap.get(CONTEXT_ENTRY_REFUSAL_PARAM) === CONTEXT_ENTRY_REFUSAL_VALUE;

  /** The reissue round-trip is in flight. */
  readonly finishing = signal(false);
  /** A failure of the reissue itself (the panel reports its own). */
  readonly errorMessage = signal<string | null>(null);
  /**
   * The reissue succeeded but the session is still restricted.
   *
   * Real, not defensive padding: the gate is re-evaluated server-side, and a company on `Federated` is never
   * satisfied by an authenticator no matter how correctly it was enrolled. Saying so beats silently leaving
   * somebody on a screen whose one button they have just pressed successfully.
   */
  readonly stillRestricted = signal(false);

  /**
   * The panel reports a completed, acknowledged enrolment → ask the server for a session that reflects it.
   *
   * Bound to the panel's `enrolled` output, which fires on the **acknowledgement** of the recovery codes rather
   * than on the confirmation — navigating away the instant the factor was confirmed would take the one-and-only
   * showing of those codes off the screen before they were written down.
   */
  onEnrolled(): void {
    if (this.finishing()) return;

    // Nothing to lift. A session refused at context entry was never restricted — its token is unchanged and
    // correct, and the factor that was missing is re-read server-side at the next `Enter`. The server says
    // exactly that now: `UserService.ReissueAfterSecondFactorEnrolment` refuses a session carrying no
    // `acct_sec` claim with `E_AUTH_SECOND_FACTOR_REISSUE_NOT_APPLICABLE` (403). It used to resolve a `User` or
    // an `Employee` from the session, find a native identity to be neither, and answer 401 — which
    // `ErrorInterceptor` turns into a forced sign-out at the exact moment the person finished enrolling.
    //
    // So this guard is no longer what stands between somebody and a spurious logout, and it stays anyway: a
    // round-trip whose only possible outcome is an error panel, on a screen the person has just finished with.
    //
    // Guarded on the CLAIM rather than on the reason alone: if a genuinely restricted session ever arrived with
    // the parameter, skipping the reissue would leave it in the gate forever.
    if (this.refusedAtContextEntry && !this.auth.isAccountSecurityRestricted) {
      this.toChooser();
      return;
    }

    this.finishing.set(true);
    this.errorMessage.set(null);
    this.stillRestricted.set(false);

    this.secondFactor.reissue().subscribe({
      next: token => {
        this.auth.applyReissuedSession(token);
        this.finishing.set(false);

        if (this.auth.isAccountSecurityRestricted) {
          this.stillRestricted.set(true);
          return;
        }
        // Home, not a role-specific destination: `AuthGuard` already routes each kind of session from `/` to
        // where it belongs (an employee to its panel, a company-less platform owner to the landing). Choosing
        // here would be a second, drifting copy of that decision.
        this.router.navigate(['/']).then();
      },
      error: err => {
        this.finishing.set(false);
        this.errorMessage.set(describeSecondFactorError(
          err, 'Die Sitzung konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.'));
      }
    });
  }

  /** Retry after a failed reissue — the enrolment already exists, so only the session refresh is repeated. */
  retry(): void {
    this.onEnrolled();
  }

  /**
   * Back to the workspace chooser — offered, and navigated to after enrolling, **only** on the context-entry
   * path.
   *
   * It is not a hole in "this screen offers no way into the app". A session refused at context entry is not
   * confined: it holds no context, may enter any company that asks for nothing, and the chooser is explicitly
   * reachable by every signed-in session (`AuthGuard`, the branch above the shell redirects). Withholding it
   * would be the actual defect — an account with two companies, one strict and one not, would sit on an
   * enrolment screen it has no need of, with signing out as its only exit.
   *
   * A router navigation rather than `window.open`, unlike entering a context: the chooser is a route in the
   * shell that is already loaded, and nothing about the session has changed.
   */
  toChooser(): void {
    this.router.navigate(['/' + AppRoutes.AuthenticationChooseContext]).then();
  }

  /**
   * The only other way off this screen.
   *
   * Deliberately the plain, non-forced `logout()`: it refuses while impersonating, and that refusal must hold
   * here too. An impersonation session's only correct exit is "Stop impersonating", which closes the audit
   * session — forcing a sign-out from this screen would orphan it, and this screen has no more right to do that
   * than any other.
   */
  logout(): void {
    this.auth.logout();
  }
}
